import React, { useState, useContext, useEffect } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import styled, { ThemeContext } from 'styled-components';
import Img from 'gatsby-image';

import device from '../utils/device';
import { isDateInPast } from '../utils/date';
import SEO from '../components/Seo';
import GlobalStyle2 from '../components/GlobalStyle2';
import R from '../components/images/R';
import Logo from '../components/images/Logo';
import VideoButton from '../components/images/VideoButton';
import VideoPager from '../components/VideoPager';
import VideoCarousel from '../components/VideoCarousel';
import { formatDate } from '../utils/format';
import SalesSection from './SalesSection';

const Header = styled.header`
  background: ${({ theme }) => theme.colors.background};
  padding-bottom: 2rem;

  @media ${device.desktop} {
    padding-bottom: 8rem;
  }
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const LogoHolder = styled.h1`
  margin: 0;
  width: 6.56rem;
  margin: 1.5vw 0 1vw 0;

  @media ${device.desktop} {
    width: 6rem;
  }
`;

const Sign = styled.div`
  width: 6rem;
  margin-top: 0.5rem;
  color: ${({ theme }) => theme.colors.textLight};

  @media ${device.desktop} {
    width: 10rem;
    margin-top: 1.5rem;
  }
`;

const By = styled.div`
  font-family: 'Montserrat';
  font-size: 0.5rem;
  font-weight: 300;

  @media ${device.desktop} {
    font-size: 0.8rem;
  }
`;

const Alex = styled.div`
  font-family: 'Montserrat';
  font-weight: 900;
  font-size: 0.65rem;
  text-transform: uppercase;

  @media ${device.desktop} {
    font-size: 1rem;
  }
`;

const VideoContainer = styled.div`
  margin-top: -1.2rem;

  @media ${device.desktop} {
    margin-top: -7rem;
  }
`;

const Main = styled.div`
  background: ${({ theme }) => theme.colors.backgroundLight};
`;

const SeriesContainer = styled.div`
  color: ${({ theme }) => theme.colors.textDark};
  margin: 1.7rem 1.6rem;

  @media ${device.desktop} {
    display: flex;
    justify-content: space-between;
    color: ${({ theme }) => theme.colors.textDark};

    margin: 6rem 10vw 0 10vw;
  }
`;

const EpisodeContainer = styled.div`
  margin-bottom: 3.4rem;
  @media ${device.desktop} {
    width: 25vw;
  }
`;

const EpisodeWrapper = styled.div`
  @media ${device.maxDesktop} {
    display: flex;
  }
`;

const EpisodeImageContainer = styled.div`
  cursor: pointer;
  position: relative;
  height: fit-content;

  @media ${device.maxDesktop} {
    width: 55%;
    flex: 1;
  }
`;

const EpisodeImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  color: ${({ theme }) => theme.colors.textLight};

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  ${(p) => !p.isAvailable && 'background: rgba(255, 255, 255, 0.5);'}

  .copy {
    font-weight: 900;

    margin-top: 0.8rem;
    font-size: 0.8rem;

    @media ${device.desktop} {
      margin-top: 1.5rem;
      font-size: 1rem;
    }

    @media ${device.bigDesktop} {
      margin-top: 1.5rem;
      font-size: 1.5rem;
    }
  }
`;

const EpisodeDataContainer = styled.div`
  ${(p) => !p.isAvailable && 'opacity: 0.5;'}

  @media ${device.maxDesktop} {
    flex: 1;
    padding-left: 0.75rem;
  }
`;

const EpisodeTitle = styled.h2`
  font-weight: bold;

  font-size: 1.17rem;

  @media ${device.desktop} {
    margin-top: 2.7vw;
    font-weight: bold;
    font-size: 1.6rem;
  }

  @media ${device.bigDesktop} {
    margin-top: 2.7vw;
    font-weight: bold;
    font-size: 2rem;
  }
`;

const EpisodeMeta = styled.div`
  font-weight: 300;

  font-size: 1rem;
  margin-top: 0.2rem;

  @media ${device.desktop} {
    font-size: 1rem;
    margin-top: 0.5rem;
  }

  @media ${device.bigDesktop} {
    font-size: 1.2rem;
    margin-top: 0.5rem;
  }
`;

const EpisodeCopy = styled.div`
  font-size: 1rem;
  margin-top: 1rem;
  font-weight: 500;
  line-height: 1.4;

  ${(p) => !p.isAvailable && 'opacity: 0.5;'}

  @media ${device.desktop} {
    font-size: 1.1rem;
    margin-top: 2rem;
  }

  @media ${device.bigDesktop} {
    font-size: 1.3rem;
    margin-top: 2rem;
  }
`;

const RHolder = styled.div`
  display: flex;
  justify-content: center;

  width: 2.44rem;
  padding: 3.5rem 0;
  margin: 0 auto;

  @media ${device.desktop} {
    width: 4rem;
    padding: 6rem 0;
  }

  @media ${device.bigDesktop} {
    width: 5rem;
    padding: 7rem 0;
  }
`;

const Footer = styled.footer`
  font-weight: 300;
  text-align: center;

  color: ${({ theme }) => theme.colors.textDark};
  background; #fff;

  font-size: 0.63rem;
  padding: 2.5rem 1.6rem;

  @media ${device.desktop} {
    font-size: 1rem;
    padding: 6rem 0;
  }

  @media ${device.bigDesktop} {
    font-size: 1.5rem;
  }
`;

const VideoTease = ({ video, isActive, onClick }) => {
  const theme = useContext(ThemeContext);

  if (typeof window === 'undefined') {
    return <></>;
  }

  const isAvailable = isDateInPast(video.releaseDate);
  const overlayColor = isAvailable
    ? theme.colors.textLight
    : theme.colors.textDark;

  const overlayCopy = () => {
    if (!isAvailable) {
      return `Available ${formatDate(video.releaseDate)}`;
    }

    if (isActive) {
      return 'Playing now';
    }

    return 'Available now';
  };

  return (
    <EpisodeContainer isAvailable={isAvailable}>
      <EpisodeWrapper>
        <EpisodeImageContainer onClick={onClick}>
          <Img fluid={video.cover.childImageSharp.fluid} />
          <EpisodeImageOverlay isAvailable={isAvailable}>
            <VideoButton color={overlayColor} text={`Video ${video.id}`} />
            <div className="copy" style={{ color: overlayColor }}>
              {overlayCopy()}
            </div>
          </EpisodeImageOverlay>
        </EpisodeImageContainer>
        <EpisodeDataContainer isAvailable={isAvailable}>
          <EpisodeTitle>{video.title}</EpisodeTitle>
          <EpisodeMeta>
            with {video.with} | {video.duration}
          </EpisodeMeta>
        </EpisodeDataContainer>
      </EpisodeWrapper>
      <EpisodeCopy isAvailable={isAvailable}>{video.copy}</EpisodeCopy>
    </EpisodeContainer>
  );
};

const VideoPage = ({ index, location }) => {
  const theme = useContext(ThemeContext);
  const images = useStaticQuery(query);
  const [active, setActive] = useState(index);

  const [showInstallmentBox, setShowInstallmentBox] = useState(true);

  useEffect(() => {
    // Parse query parameters here
    const params = new URLSearchParams(window.location.search);
    const showInstallmentBoxParam = params.get('showInstallmentBox');

    // Update state based on query parameters
    setShowInstallmentBox(showInstallmentBoxParam); // Set to false if 'showInstallmentBox' is 'false'
  }, []);

  const episodes = [
    {
      id: 1,
      vimeoId: 458904054,
      title: 'The science of how trauma affects your body',
      shortTitle: 'How trauma affects your body',
      with: 'Alex Howard',
      duration: '52 mins',
      copy:
        'If you are suffering with fatigue, sleep issues, chronic pain, anxiety, trauma, or many other related issues, there is a direct impact between stress, trauma, and your physical body.  In this first video, Alex explains the science of this relationship in a simple and accessible way.',
      cover: images.video1,
      releaseDate: new Date(2022, 0, 1),
    },
    {
      id: 2,
      vimeoId: 460060720,
      title: 'What creates a maladaptive stress response?',
      shortTitle: 'What creates a maladaptive stress response?',
      with: 'Alex Howard',
      duration: '54 mins',
      copy:
        'A maladaptive stress response directly impacts upon the healing capacity of your physical and emotional bodies. In this second video, Alex explains the key ingredients that create and perpetuate a maladaptive stress response.',
      cover: images.video2,
      releaseDate: new Date(2022, 0, 24),
    },
    {
      id: 3,
      vimeoId: 461081166,
      title:
        'How to RESET your nervous system for physical and emotional healing',
      shortTitle: 'How to RESET your nervous system',
      with: 'Alex Howard',
      duration: '56 mins',
      copy:
        'In this final video, Alex introduces the RESET model of how to switch off a maladaptive stress response and cultivate an optimum health state.  You will also learn about Alex’s groundbreaking RESET online coaching program.',
      cover: images.video3,
      releaseDate: new Date(2022, 0, 27),
    },
  ];

  const showSales = new Date() > episodes[episodes.length - 1].releaseDate;

  return (
    <>
      <GlobalStyle2 />
      <SEO title="The RESET program" />
      <Main>
        <Header>
          <TitleRow>
            <Sign />
            <LogoHolder>
              <Logo width="100%" color={theme.colors.white} />
            </LogoHolder>
            <Sign>
              <By>A program by</By>
              <Alex>Alex Howard</Alex>
            </Sign>
          </TitleRow>
          <VideoPager videos={episodes} active={active} setActive={setActive} />
        </Header>

        <VideoContainer>
          <VideoCarousel
            videos={episodes}
            active={active}
            setActive={setActive}
          />
        </VideoContainer>

        {!showSales || active <= 2 ? (
          <SeriesContainer>
            {episodes.map((e) => (
              <VideoTease
                key={e.id}
                video={e}
                isActive={e.id === active}
                onClick={() => setActive(e.id)}
              />
            ))}
          </SeriesContainer>
        ) : (
          <SalesSection showInstallmentBox={showInstallmentBox} />
        )}

        <RHolder>
          <R width="100%" color={theme.colors.actionPrimaryLight} />
        </RHolder>
      </Main>

      <Footer>
        <div>
          This website and its content are copyright &copy; Alex Howard{' '}
          {new Date().getFullYear()}.
        </div>
        <div> All Rights Reserved.</div>
      </Footer>
    </>
  );
};

const query = graphql`
  query {
    video1: file(relativePath: { eq: "video1.png" }) {
      childImageSharp {
        fluid(maxWidth: 1600) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    video2: file(relativePath: { eq: "video2.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1600) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    video3: file(relativePath: { eq: "video3.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1600) {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`;

export default VideoPage;
